import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import FirstTimeSetup from "@/pages/FirstTimeSetup";
import CaregiverDashboard from "@/pages/CaregiverDashboard";
import FrontlineWorkerDashboard from "@/pages/FrontlineWorkerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import DistrictAdminDashboard from "@/pages/DistrictAdminDashboard";
import ChildProfile from "@/pages/ChildProfile";
import Screening from "@/pages/Screening";
import Interventions from "@/pages/Interventions";
import BulkUpload from "@/pages/BulkUpload";
import BulkUserUpload from "@/pages/BulkUserUpload";
import AdminUserCreation from "@/pages/AdminUserCreation";
import AuditLogs from "@/pages/AuditLogs";
import UserManagement from "@/pages/UserManagement";
import Notifications from "@/pages/Notifications";
import RiskNotifications from "@/pages/RiskNotifications";
import SpeechAnalysis from "@/pages/SpeechAnalysis";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="text-xl font-bold text-[#0F4C81]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />} 
          />
          
          <Route 
            path="/forgot-password" 
            element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} 
          />
          
          <Route 
            path="/reset-password" 
            element={!user ? <ResetPassword /> : <Navigate to="/dashboard" />} 
          />
          
          <Route 
            path="/setup" 
            element={<FirstTimeSetup />} 
          />
          
          <Route 
            path="/dashboard" 
            element={
              user ? (
                user.role === 'caregiver' ? (
                  <CaregiverDashboard user={user} onLogout={handleLogout} />
                ) : user.role === 'frontline_worker' ? (
                  <FrontlineWorkerDashboard user={user} onLogout={handleLogout} />
                ) : user.role === 'super_admin' || user.is_super_admin ? (
                  <SuperAdminDashboard user={user} onLogout={handleLogout} />
                ) : user.role === 'district_admin' ? (
                  <DistrictAdminDashboard user={user} onLogout={handleLogout} />
                ) : (
                  <AdminDashboard user={user} onLogout={handleLogout} />
                )
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          
          <Route 
            path="/child/:childId" 
            element={user ? <ChildProfile user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/screening/:childId" 
            element={user ? <Screening user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/interventions/:childId" 
            element={user ? <Interventions user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/speech/:childId" 
            element={user ? <SpeechAnalysis user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/bulk-upload" 
            element={user && ['frontline_worker', 'administrator', 'state_admin', 'district_admin', 'super_admin'].includes(user.role) ? <BulkUpload user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/bulk-user-upload" 
            element={user && ['administrator', 'state_admin', 'district_admin', 'super_admin'].includes(user.role) ? <BulkUserUpload user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/admin/create-user" 
            element={user && ['state_admin', 'district_admin', 'super_admin'].includes(user.role) || user?.is_super_admin ? <AdminUserCreation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/audit-logs" 
            element={user && ['administrator', 'state_admin', 'super_admin'].includes(user.role) ? <AuditLogs user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/user-management" 
            element={user && ['administrator', 'state_admin', 'super_admin'].includes(user.role) ? <UserManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/notifications" 
            element={user ? <Notifications user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/risk-notifications" 
            element={user && ['administrator', 'state_admin', 'district_admin', 'super_admin'].includes(user.role) ? <RiskNotifications user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route 
            path="/super-admin"
            element={user && (user.role === 'super_admin' || user.is_super_admin) ? <SuperAdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          
          <Route 
            path="/dashboard/admin" 
            element={user && ['administrator', 'state_admin', 'district_admin', 'super_admin'].includes(user.role) ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;